import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CurrencyContext = createContext(null);

export const useCurrency = () => useContext(CurrencyContext);

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => {
    return localStorage.getItem('preferred_currency') || 'CAD';
  });
  const [usdMarkup, setUsdMarkup] = useState(15); // Default 15% markup
  const [exchangeRate, setExchangeRate] = useState(0.73); // Default CAD to USD rate (~0.73)
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [showCountryNotification, setShowCountryNotification] = useState(false);
  
  useEffect(() => {
    // Fetch markup from settings
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/settings`);
        if (res.data.usd_markup_percent) {
          setUsdMarkup(res.data.usd_markup_percent);
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
    };
    fetchSettings();
    
    // Fetch live exchange rate (CAD to USD)
    const fetchExchangeRate = async () => {
      try {
        // Using a free exchange rate API
        const res = await axios.get('https://api.exchangerate-api.com/v4/latest/CAD');
        if (res.data && res.data.rates && res.data.rates.USD) {
          setExchangeRate(res.data.rates.USD);
        }
      } catch (error) {
        console.error('Failed to fetch exchange rate, using default:', error);
        // Keep default rate of 0.73
      }
    };
    fetchExchangeRate();
    
    // Auto-detect country based on IP (only on first visit)
    const detectCountry = async () => {
      // Skip if user already has a preference set
      const hasPreference = localStorage.getItem('preferred_currency');
      const hasDetected = localStorage.getItem('country_detected');
      
      if (hasPreference || hasDetected) {
        return;
      }
      
      try {
        // Using ipapi.co (free, HTTPS-enabled IP geolocation API)
        const res = await axios.get('https://ipapi.co/json/');
        
        if (res.data && res.data.country_code) {
          const countryCode = res.data.country_code;
          const countryName = res.data.country_name;
          
          localStorage.setItem('country_detected', 'true');
          setDetectedCountry(countryName);
          
          // Set currency based on detected country
          if (countryCode === 'US') {
            setCurrency('USD');
            localStorage.setItem('preferred_currency', 'USD');
            setShowCountryNotification(true);
          } else if (countryCode === 'CA') {
            setCurrency('CAD');
            localStorage.setItem('preferred_currency', 'CAD');
            setShowCountryNotification(true);
          }
          // For other countries, default to CAD (base currency)
        }
      } catch (error) {
        console.error('Failed to detect country:', error);
        // Silently fail - user can manually select currency
      }
    };
    detectCountry();
  }, []);

  const changeCurrency = (newCurrency) => {
    setCurrency(newCurrency);
    localStorage.setItem('preferred_currency', newCurrency);
    setShowCountryNotification(false); // Hide notification when user manually changes
  };
  
  const dismissCountryNotification = useCallback(() => {
    setShowCountryNotification(false);
  }, []);

  // Convert price from CAD (base) to selected currency
  // Formula: USD = (CAD * exchange_rate) * (1 + markup%)
  const convertPrice = (cadPrice) => {
    if (currency === 'USD') {
      // Convert CAD to USD using exchange rate, then add markup
      const usdBase = cadPrice * exchangeRate;
      const usdWithMarkup = usdBase * (1 + usdMarkup / 100);
      return usdWithMarkup;
    }
    return cadPrice; // CAD is base
  };

  // Format price with currency symbol
  const formatPrice = (cadPrice) => {
    const converted = convertPrice(cadPrice);
    if (currency === 'USD') {
      return `$${converted.toFixed(2)} USD`;
    }
    return `$${cadPrice.toFixed(2)} CAD`;
  };

  // Get just the symbol
  const getCurrencySymbol = () => {
    return '$';
  };

  // Get currency suffix
  const getCurrencySuffix = () => {
    return currency;
  };

  return (
    <CurrencyContext.Provider value={{
      currency,
      changeCurrency,
      convertPrice,
      formatPrice,
      getCurrencySymbol,
      getCurrencySuffix,
      usdMarkup,
      exchangeRate,
      detectedCountry,
      showCountryNotification,
      dismissCountryNotification
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};
